import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ICountry, IDepartament, ILanguage, IMunicipality, IProvince, IUser } from '@core/models';
import { Language, Ubication, User } from '@core/services';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { TermsPrivacyPolicy } from '@shared/components';
import { touchControls } from '@shared/utils';
import { ConfirmPasswordValidator } from '@shared/validators';
import moment from 'moment';
import { filter, firstValueFrom, startWith } from 'rxjs';
import { RegisterSuccess } from '../register-success/register-success';
import { Authentication } from '@core/services';
import { FormErrorComponent } from '@shared/components/form-error/form-error.component';

/** constante que contiene el código de colombia*/
const COLOMBIA_CODE = 'COL';

/**
 * ### **RegisterComponent**
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar el formulario de registro y de enviar los datos al servidor.
 */
@Component({
  selector: 'app-register-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    TermsPrivacyPolicy,
    FormErrorComponent,
  ],
  templateUrl: './register-form.html',
  styleUrl: './register-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterForm implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(User);
  private readonly ubicationService = inject(Ubication);
  private readonly authService = inject(Authentication);
  private readonly languageService = inject(Language);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly auth = signal(this.authService.getAuthInformation());
  /**
   * Variable que recibe el modal de un componente padre o superior a el.
   */
  readonly modalRef = input.required<any>();

  /** variable que recibe los datos del usuario de un componente padre */
  readonly user = input<IUser | null>(null);

  /** decorador que sirve para representan un componente obteniendo los elementos nativos y directivas */
  private readonly termsPrivacy = viewChild(TermsPrivacyPolicy);

  /**
   * Variable que guardara y emitira si un usuario se ha registrado.
   */
  readonly userRegistered = output<any>();

  /**
   * Variable que controlara y guardar los datos del formulario de registro de usuario.
   */
  protected readonly registerForm = signal<FormGroup>(this.fb.group({}));

  /**
   * Variable que guardara el estado de aceptación de los terminos y condiciones.
   */
  protected readonly acceptTerms = signal<FormControl>(
    new FormControl(false, [Validators.requiredTrue]),
  );

  /** variable que guardara la lista de paises*/
  protected readonly countries = signal<ICountry[]>([]);
  /** variable que guardara la lista de departamentos */
  protected readonly departments = signal<IDepartament[]>([]);
  /** Arreglo que guardará la lista de provincicas */
  // province!: IProvince[];
  /** Arreglo que guardará la lista de municipios */
  protected readonly municipalities = signal<IMunicipality[]>([]);
  /** Arreglo que guardara la lista de idiomas */
  protected readonly languages = signal<ILanguage[]>([]);
  /** Variable que guardará el estado de existencia de un usuario. */
  protected readonly userExists = signal<boolean>(false);
  /** Variable que guardara la fecha de cumpleaños del usuario a registrar */
  private birthdate!: any;
  /** Variable que contendra el prefijo del número telefónico */
  // prefix!: string;
  /**  variable que hace referencia a una persona que es de colombia (True) o no (False). */
  protected readonly IS_COLOMBIA = signal<boolean>(false);

  protected passwordVisibility = signal({
    password: false,
    confirmPassword: false,
  });

  /**
   *  Función nativa que inicializa variables y funciones que se requieran para dar inicio a la carga del componente.
   * @param fb Parametro que hara uso de las funciones de Formbuilder y gestionar formularios.
   * @param userService Parametro que hara uso de las funciones de UserService
   * @param ubicationService Parametro
   * @param languageService Parametro
   * @param authService Parametro que hara uso de las funciones de authService para autenticación.
   * @param dialog Parametro que contara con las caracteristicas proporcionadas por MatDialog.
   */
  constructor() {
    this.getCountries();
    this.getLanguages();
  }

  /**
   * Función nativa de angular que se activa una vez se empieza a cargar el componenten, en este caso
   * ejecuta la funcion this.createForm()
   */
  ngOnInit(): void {
    this.createForm();
    // console.log('info usuario', this.auth());
    const user = this.auth();
    if (user?.country?.name?.toLowerCase() === 'colombia') {
      this.IS_COLOMBIA.set(true);
    }
  }

  /**
   * Función nativa de angular que se ctiva una vez ha detectado un cambio sobre las variables de tipo @Input
   * @param changes parametro que detecta los cambios.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && changes.user.currentValue) {
      if (this.user()) {
        this.acceptTerms().setValue(true);
      }
    }
  }

  protected togglePassword(field: 'password' | 'confirmPassword'): void {
    this.passwordVisibility.update((state) => ({
      ...state,
      [field]: !state[field],
    }));
  }

  /**
   * Función que obtiene y le da formato a la fecha de cuumpleaños del usuario a registrar.
   * @param birthdate fecha de cumpleaños del usuario.
   */
  private getBirthdate(birthdate: string): {
    year: number;
    month: number;
    day: number;
  } {
    const momentBirthdate = moment(birthdate, 'YYYY-MM-DD');
    return {
      year: momentBirthdate.year(),
      month: momentBirthdate.month() + 1,
      day: momentBirthdate.date(),
    };
  }

  /**
   * funcion encargada de crear el formulario con sus respectivos controls para la debida recaudacion de los datos.
   */
  private createForm(): void {
    const user = this.user();
    if (user) {
      this.birthdate = user.birthdate;
    }

    const form = this.fb.group(
      {
        username: [user ? user.username : null, Validators.required],
        last_name: [user ? user.last_name : null, Validators.required],
        country: [user && user.country ? user.country.id : null, Validators.required],
        first_name: [user ? user.first_name : null, Validators.required],
        region: [user ? user.region : null],
        state: [user ? user.state : null],
        city: [user ? user.city : null],
        re_password: [null, !user ? Validators.required : []],
        email: [
          user ? user.email : null,
          [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
          ],
        ],
        is_superuser: [user ? user.is_superuser : false],
        password: [null, !user ? Validators.required : []],
        language: [user ? user.language : null, Validators.required],
        phone_code: [user ? user.phone_code : null, [Validators.required]],
        phone: [
          user ? user.phone : null,
          [Validators.required, Validators.pattern(/^-?(0|[1-9]\d*)?$/)],
        ],
        gender: [user ? user.gender : null, Validators.required],
        birthdate: [user ? this.getBirthdate(user.birthdate) : null, Validators.required],
      },
      {
        validator: ConfirmPasswordValidator.MatchPassword,
      },
    );
    this.registerForm.set(form);
    this.valueChanges();

    if (user && user.phone_code) {
      form.get('phone_code')?.setValue(user.phone_code);
    }
  }

  /**
   * Función encargada de detectar si han habido cambios en el formulario de registro, en este caso lo que se hace
   * es suscribirse a varios elementos del formulario en los cuales puede haber algún cambio y afectar a otros elementos.
   */
  private valueChanges(): void {
    const form = this.registerForm();
    let initialized = false;
    form
      .get('country')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(form.get('country')?.value),
      )
      .subscribe((value) => {
        if (initialized) {
          form.get('state')?.reset();
          form.get('city')?.reset();
          this.departments.set([]);
          this.municipalities.set([]);
        }
        if (this.countries()) {
          const country = this.countries().find((c) => c.id === value);
          if (country) {
            form.get('phone_code')?.setValue(country.phone_code);
            if (country.alfa_three === COLOMBIA_CODE) {
              this.IS_COLOMBIA.set(true);
            } else {
              this.IS_COLOMBIA.set(false);
            }
            form.get('state')?.setValidators(null);
            form.get('city')?.setValidators(null);
            form.updateValueAndValidity();
          } else {
            form.get('phone_code')?.setValue(null);
          }
        }
        if (value) {
          this.getDepartments(value);
        }
        form.updateValueAndValidity();
        initialized = true;
      });

    form
      .get('state')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(form.get('state')?.value),
        filter((value) => !!value),
      )
      .subscribe((value) => {
        console.log('value: ' + value);
        this.getMunicipalities(value);
      });

    // this.registerForm
    //   .get('username')
    //   ?.valueChanges.pipe(takeUntil(this.unsubscribe))
    //   .subscribe((value) => {
    //     this.validUniqueUser(value);
    //   });

    // this.registerForm
    //   .get('email')
    //   ?.valueChanges.pipe(takeUntil(this.unsubscribe))
    //   .subscribe((value) => {
    //     this.validUniqueEmail(value);
    //   });
  }

  /**
   * Función encargada de ejecutar el registro de usuario, para esto el mismo debio acpetar los terminos y condiciones y el formulario debe
   * ser valido, en caso de haber un usuario existente el metodo detecta que esta editando la infromacion del mismo y
   * no esta agregando un usuario.
   */
  protected registerUser(): void {
    const form = this.registerForm();
    console.log('registerUser', form);
    if (form.invalid) {
      touchControls(form);
      form.updateValueAndValidity();
      this.acceptTerms().markAsTouched();
      return;
    }

    console.log('this.acceptTerms()', this.acceptTerms());
    console.log('form.valid', form.valid);
    console.log('this.userExists()', this.userExists());

    if (this.acceptTerms().valid && form.valid && !this.userExists()) {
      if (this.user()) {
        const registerForm = form.getRawValue();
        const birthdate = registerForm['birthdate'];
        registerForm['birthdate'] = `${birthdate.year}-${birthdate.month}-${birthdate.day}`;
        delete registerForm['password'];
        delete registerForm['re_password'];
        this.editUser(registerForm);
      } else {
        const registerForm = form.getRawValue();
        const birthdate = registerForm['birthdate'];
        registerForm['birthdate'] = `${birthdate.year}-${birthdate.month}-${birthdate.day}`;
        this.addUser(registerForm);
      }
    } else {
      touchControls(form);
      this.acceptTerms().markAsTouched();
    }
  }

  /**
   * Función encargado de abrir el modal de Terminos y Condiciones (TC).
   * @param terms Parametro que contiene el modal de TC.
   */
  protected openTermsOrPrivacy(terms: boolean): void {
    this.termsPrivacy()?.open(terms);
  }

  /**
   * Función encargada de agregar el usuario, en caso dde ya existir cambia el estado de this.userExists a true.
   * @param user Objeto con la información del usuario a registrar.
   */
  private async addUser(user: IUser): Promise<void> {
    console.log('addUser', user);
    try {
      const response = await firstValueFrom(this.userService.add(user));
      console.log('response', response);
      this.modalRef().dismiss();
      this.userRegistered.emit(response);
      this.showRegisterSuccess();
    } catch (error) {
      console.error(error);
      this.userExists.set(true);
    }
  }

  /**
   * Función encargada modificar o editar la información del usuario.
   * @param user Objeto con la información a editar el usuario.
   */
  private async editUser(user: IUser): Promise<void> {
    try {
      const response = await firstValueFrom(this.userService.edit(Number(this.user()?.id), user));
      this.modalRef().dismiss();
      // this.userRegistered.emit(response);
      this.showRegisterSuccess();
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Función encargada de obtener la lista de paises y enviarselos al formulario,
   * para esto hace uso de la funcion getCountries del servicio UbicationService.
   */
  private async getCountries(): Promise<void> {
    try {
      const form = this.registerForm();
      const countries = await firstValueFrom(this.ubicationService.getCountries());
      this.countries.set(countries);
      if (countries && form) {
        form.get('country')?.setValue(form.get('country')?.value);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Función encargada de obtener los departamentos de un país en específico.
   * @param country Parametro que recibe el id del pais.
   */
  private async getDepartments(country: number): Promise<void> {
    try {
      const departments = await firstValueFrom(this.ubicationService.getDepartmentsAll());
      this.departments.set(departments);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Función encargada de obtener los municipios de un departamento en específico.
   * @param department Parametro que recibe el identificador del municipio.
   */
  private async getMunicipalities(department: number): Promise<void> {
    try {
      const municipalities = await firstValueFrom(
        this.ubicationService.getNewMunicipalities(department),
      );
      this.municipalities.set(municipalities);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Función encargada de obtener los idiomas existen actualmente.
   */
  private async getLanguages(): Promise<void> {
    try {
      const languages = await firstValueFrom(this.languageService.getLanguages());
      this.languages.set(languages);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Función encargada de validar si el nombre usuario no esta repetido, para esto se apoya del
   * servicio UserService para darle un estado repsectivo a userExists y determinar si el nombre esa ocupado.
   * @param username Parametro que recibira el nombre de usuario.
   */
  protected async validUniqueUser(username: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.userService.validUniqueUSer(username));
      if (this.user()) {
        if (this.user()?.username === username.trim()) {
          this.userExists.set(false);
        } else {
          this.userExists.set(true);
        }
      } else {
        this.userExists.set(true);
      }
    } catch (error) {
      console.error(error);
      this.userExists.set(false);
    }
  }

  /**
   * Función encargada de validar que el email sea unico, para esto hace uso del USerService y darle un estado repsectivo a userExists y
   * determinar si el email esta ocupado.
   * @param email Parametro que recibira el email ingresado por el usuario a registrarse.
   */
  protected async validUniqueEmail(email: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.userService.validUniqueEmail(email));
      if (this.user()) {
        if (this.user()?.email === email.trim()) {
          this.userExists.set(false);
        } else {
          this.userExists.set(true);
        }
      } else {
        this.userExists.set(true);
      }
    } catch (error) {
      console.error(error);
      this.userExists.set(false);
    }
  }

  /**
   * Función encargada de abrir el modal de confirmación de registro de usuario
   * además de enviarle la informacion del mismo.
   */
  private showRegisterSuccess(): void {
    const config = {
      data: {
        edited: !!this.user(),
      },
    };
    const dialogRef = this.dialog.open(RegisterSuccess, config);
  }
}
